import React, {
  createRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeftRounded, QrCode } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { decryptURL, findOnArray } from 'utils/functions';
import { AuthContext } from 'contexts/AuthContext';
import { DropsContext } from 'contexts/DropsContext';
import { ExpedicoesContext } from 'contexts/ExpedicoesContext';
import Container from 'components/Container';
import Button from 'components/Button';
import Input from 'components/Input';
import Card from 'components/Card';
import localeText from 'utils/localeText';
import styles from './styles';

const Tab0 = ({ bip1, setBip1 }) => {
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current.focus();
  }, []);

  const handleBlur = () => inputRef.current.focus();

  const handleChange = (e) => setBip1(e?.target?.value);

  return (
    <Grid item xs={12} display="flex" alignItems="center" gap={5}>
      <QrCode sx={styles?.qrcode} color="primary" />
      <Typography variant="body1" fontWeight="bold" flex={1}>
        Faça a leitura do endereço de estoque
      </Typography>
      <input
        ref={inputRef}
        value={bip1}
        onBlur={handleBlur}
        onChange={handleChange}
        style={styles?.hideInput}
      />
    </Grid>
  );
};

const Tab1 = ({ bip1, bip2, setBip2 }) => {
  const inputRef = useRef(null);
  const { drops } = useContext(DropsContext);
  const etqend = Boolean(bip1) ? JSON.parse(bip1) : null;
  const endereco = findOnArray(etqend?.ceendereco_id, drops?.EnderecoEstoque);

  useEffect(() => {
    inputRef.current.focus();
  }, []);

  const handleBlur = () => inputRef.current.focus();

  const handleChange = (e) => setBip2(e?.target?.value);

  return (
    <Grid item xs={12} display="flex" alignItems="center" gap={5}>
      <QrCode sx={styles?.qrcode} color="primary" />
      <Box flex={1} gap={2} display="flex" flexDirection="column">
        {Boolean(endereco) && (
          <Typography variant="body1" fontWeight="bold" flex={1}>
            {endereco?.tipo && `${endereco?.tipo} - `}
            {endereco?.label}
          </Typography>
        )}
        <Typography variant="body1" fontWeight="bold" flex={1}>
          Faça a leitura do produto
        </Typography>
      </Box>
      <input
        ref={inputRef}
        value={bip2}
        onBlur={handleBlur}
        onChange={handleChange}
        style={styles?.hideInput}
      />
    </Grid>
  );
};

const Tab2 = ({ bip1, bip2, bip3, setBip3, handleSubmit, loading }) => {
  const inputRef = useRef(null);
  const { drops } = useContext(DropsContext);
  const etqend = Boolean(bip1) ? JSON.parse(bip1) : null;
  const etqProduto = Boolean(bip2) ? JSON.parse(bip2) : null;
  const endereco = findOnArray(etqend?.ceendereco_id, drops?.EnderecoEstoque);
  const produto = findOnArray(etqProduto?.produto_id, drops?.Produto);

  useEffect(() => {
    inputRef.current.focus();
  }, []);

  return (
    <Grid item xs={12} display="flex" alignItems="center" gap={5}>
      <QrCode sx={styles?.qrcode} color="primary" />
      <Box flex={1} gap={2} display="flex" flexDirection="column">
        {Boolean(endereco) && (
          <Typography variant="body1" fontWeight="bold" flex={1}>
            {endereco?.tipo && `${endereco?.tipo} - `}
            {endereco?.label}
          </Typography>
        )}
        <Typography variant="body1" fontWeight="bold" flex={1}>
          {produto?.label}
        </Typography>
        <Box>
          <Input
            inputRef={inputRef}
            value={bip3}
            onChange={(event) => setBip3(event?.target?.value)}
            label="Quantidade"
            type="number"
            fullWidth={false}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSubmit();
              }
            }}
          />
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          sx={styles?.btn}
          loading={loading}
        >
          Confirmar
        </Button>
      </Box>
    </Grid>
  );
};

const Separacao = () => {
  const navigate = useNavigate();
  const { hash } = useParams();
  const lotes = decryptURL(hash);
  const { user } = useContext(AuthContext);
  const enderecamento = Boolean(user?.configuracoes?.Expedicao?.enderecamento);
  const [tab, setTab] = useState(enderecamento ? 0 : 1);
  const [itens, setItens] = useState([]);
  const [pedidos, setPedidos] = useState([]);
  const [pedido, setPedido] = useState(null);
  const [bip1, setBip1] = useState('');
  const [bip2, setBip2] = useState('');
  const [bip3, setBip3] = useState('1');
  const { getSeparacao, separacaoLoading, postSeparacao, postLoading } =
    useContext(ExpedicoesContext);
  const loading = postLoading || separacaoLoading;
  const _itens = itens?.filter((f) =>
    Boolean(pedido) ? f?.pedido_id === pedido : true
  );

  const fetchData = (data = []) => {
    setItens(data);
    const arr = [...new Set(data?.map((item) => item?.pedido_id))];
    setPedidos([
      { value: null, label: 'Todos' },
      ...arr?.map((p) => {
        const pd = data?.find((item) => item?.pedido_id === p);
        return { value: p, label: pd?.pedido, Marketplace: pd?.marketplace };
      }),
    ]);
  };

  const loadData = () => {
    const params = { lotes: lotes?.map((l) => l?.id) };
    getSeparacao({ params, cb: fetchData });
  };

  const handleSubmit = () => {
    const data = {
      lotes: lotes?.map((l) => l?.id),
      endereco: Boolean(bip1) ? JSON.parse(bip1) : null,
      produto: Boolean(bip2) ? JSON.parse(bip2) : null,
      quantidade: Number(bip3),
      pedido_id: pedido,
    };
    postSeparacao({
      data,
      cb: () => {
        setTab(0);
        setBip1('');
        setBip2('');
        setBip3('1');
      },
    });
  };

  const onBack = () => {
    if (tab === 0) {
      navigate(-1);
    }
    if (tab === 1) {
      if (enderecamento) {
        setTab(0);
      } else {
        navigate(-1);
      }
      setBip1('');
      setBip2('');
    }
    if (tab === 2) {
      setTab(1);
      setBip2('');
      setBip3('1');
    }
  };

  useEffect(() => {
    if (!bip1) return;

    const timeout = setTimeout(() => {
      setTab(1);
    }, 1000);

    return () => clearTimeout(timeout);
  }, [bip1]);

  useEffect(() => {
    if (!bip2) return;

    const timeout = setTimeout(() => {
      setTab(2);
    }, 1000);

    return () => clearTimeout(timeout);
  }, [bip2]);

  useEffect(() => {
    if (tab === 0) {
      loadData();
    }
  }, [tab]);

  const RenderTabs = () => {
    const props = {
      bip1,
      bip2,
      bip3,
      setBip1,
      setBip2,
      setBip3,
      loading,
      handleSubmit,
    };
    switch (tab) {
      case 0:
        return <Tab0 {...props} />;
      case 1:
        return <Tab1 {...props} />;
      case 2:
        return <Tab2 {...props} />;
      default:
        return null;
    }
  };

  return (
    <Container>
      <Box sx={styles?.headerContainer}>
        <IconButton
          size="small"
          color="primary"
          onClick={onBack}
          sx={styles?.backBtn}
        >
          <ChevronLeftRounded />
        </IconButton>
        <Typography variant="h5" sx={styles?.title}>
          Separação
        </Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card>
            <Grid container spacing={2}>
              <RenderTabs />
              <Grid item xs={12} display="flex" justifyContent="flex-end">
                <FormControl size="small" sx={styles?.formControl}>
                  <InputLabel size="small" id="pedido_id">
                    Pedido
                  </InputLabel>
                  <Select
                    labelId="pedido_id"
                    size="small"
                    value={pedido}
                    label="Pedido"
                    onChange={(e) => setPedido(e?.target?.value)}
                  >
                    {pedidos?.map((p) => (
                      <MenuItem value={p?.value}>
                        {Boolean(p?.value) && `Pedido Nº `}
                        {p?.label || '-'}
                        {p?.Marketplace && ` (${p?.Marketplace})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sx={styles?.scroll} textAlign="center">
                {Boolean(_itens?.length) ? (
                  <DataGrid
                    rows={_itens}
                    columns={[
                      {
                        field: 'referencia',
                        headerName: 'Referência',
                        width: 150,
                        sortable: false,
                      },
                      {
                        field: 'descricao',
                        headerName: 'Descrição',
                        flex: 1,
                        minWidth: 300,
                        sortable: false,
                      },
                      {
                        field: 'variacao',
                        headerName: 'Variação',
                        width: 200,
                        sortable: false,
                      },
                      {
                        field: 'localizacao',
                        headerName: 'Localização',
                        width: 300,
                        sortable: false,
                        renderCell: ({ row }) => {
                          return (
                            <Box
                              display="flex"
                              flexDirection="column"
                              flex={1}
                              gap={1}
                              my={1}
                            >
                              {row?.localizacao?.map((m) => (
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  flex={1}
                                  gap={2}
                                >
                                  <Typography
                                    variant="caption"
                                    textAlign="end"
                                    fontWeight="bold"
                                    flex={1}
                                  >
                                    {m?.quantidade - m?.quantidade_separada}{' '}
                                    {row?.um}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    textAlign="start"
                                    flex={4}
                                  >
                                    {m?.endereco}
                                    {m?.endereco1 ? ` -> ${m?.endereco1}` : ''}
                                    {m?.endereco2 ? ` -> ${m?.endereco2}` : ''}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          );
                        },
                      },
                      {
                        field: 'quantidade',
                        headerName: 'Quantidade',
                        width: 200,
                        type: 'number',
                        sortable: false,
                        valueGetter: ({ row }) =>
                          `${row?.quantidade_separada || 0} / ${
                            row?.quantidade || 0
                          } ${row?.um || ''}`,
                      },
                      {
                        field: 'progresso',
                        headerName: 'Progresso',
                        width: 200,
                        type: 'number',
                        sortable: false,
                        renderCell: ({ row }) => {
                          const progress = Math.round(
                            ((row?.quantidade_separada || 0) /
                              (row?.quantidade || 0)) *
                              100
                          );
                          return (
                            <Box flex={1} display="flex" alignItems="center">
                              <LinearProgress
                                variant="determinate"
                                value={progress}
                                sx={styles?.linear}
                                color={progress < 100 ? 'warning' : 'success'}
                              />
                              <Typography variant="body2">
                                {progress}%
                              </Typography>
                            </Box>
                          );
                        },
                      },
                    ]}
                    hideFooter
                    autoHeight
                    density="compact"
                    loading={loading}
                    disableColumnMenu
                    showCellRightBorder
                    getRowHeight={() => 'auto'}
                    disableSelectionOnClick
                    showColumnRightBorder
                    localeText={localeText}
                    components={{ LoadingOverlay: LinearProgress }}
                  />
                ) : separacaoLoading ? (
                  <LinearProgress />
                ) : (
                  <Typography variant="caption">
                    Nenhum item encontrado
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Separacao;
